import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, ImageIcon, Plus, Check, Upload, AlertTriangle } from 'lucide-react';
import { UploadedImage, formatBytes, estimateOutputSize, ImageFormat } from '../services/imageService';

interface UploadedImagesTableProps {
  images: UploadedImage[];
  onRemove: (id: string) => void;
  onPreview: (url: string) => void;
  onUploadMore: () => void;
  onConvert: () => void;
  onClear: () => void;
  isConverting: boolean;
  selectedFormat: ImageFormat;
  compressionLevel: number;
  conversionProgress?: Record<string, number>;
}

const UploadedImagesTable: React.FC<UploadedImagesTableProps> = ({
  images,
  onRemove,
  onPreview,
  onUploadMore,
  onConvert,
  onClear,
  isConverting,
  selectedFormat,
  compressionLevel,
  conversionProgress = {}
}) => {
  const { t } = useTranslation();

  if (images.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-card border-2 border-dashed border-gray-300">
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900">No images uploaded</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by uploading your first image</p>
        <div className="mt-6">
          <button
            onClick={onUploadMore}
            className="btn btn-primary"
          >
            <Plus size={16} aria-hidden="true" />
            Upload images
          </button>
        </div>
      </div>
    );
  }

  return (
    <section aria-labelledby="uploaded-images">
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h2 id="uploaded-images" className="text-xl flex items-center m-0">
                <ImageIcon className="mr-2 text-primary-500" size={20} aria-hidden="true" />
                {t('uploadedImages.title')}
              </h2>
              <button 
                onClick={onUploadMore}
                className="ml-4 btn btn-secondary"
                aria-label={t('common.uploadMore')}
              >
                <Plus size={16} aria-hidden="true" />
                {t('common.uploadMore')}
              </button>
            </div>
            
            <div className="flex space-x-3">
              <button 
                onClick={onConvert}
                disabled={isConverting}
                className="btn btn-primary"
                aria-busy={isConverting ? "true" : "false"}
                aria-label={isConverting ? t('common.converting') : t('common.convert')}
              >
                {isConverting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-dark-purple mr-2" aria-hidden="true"></div>
                    {t('common.converting')}
                  </>
                ) : (
                  <>
                    <Check size={16} aria-hidden="true" />
                    {t('common.convert')}
                  </>
                )}
              </button>
              <button 
                onClick={onClear}
                className="btn btn-secondary"
                aria-label={t('common.clearAll')}
              >
                <X size={16} aria-hidden="true" />
                {t('common.clearAll')}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table" aria-labelledby="uploaded-images">
            <thead className="table-header">
              <tr>
                <th scope="col" className="px-6 py-4">{t('uploadedImages.headers.preview')}</th>
                <th scope="col" className="px-6 py-4">{t('uploadedImages.headers.fileName')}</th>
                <th scope="col" className="px-6 py-4">{t('uploadedImages.headers.size')}</th>
                <th scope="col" className="px-6 py-4">{t('uploadedImages.headers.estimatedSize')}</th>
                <th scope="col" className="px-6 py-4">{t('uploadedImages.headers.actions')}</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {images.map((image) => {
                const estimatedSize = estimateOutputSize(image.size, selectedFormat, compressionLevel);
                const estimatedSizeFormatted = formatBytes(estimatedSize);
                const progress = conversionProgress[image.file.name];
                const showProgress = isConverting && progress !== undefined;

                return (
                  <React.Fragment key={image.id}>
                    <tr className="table-row">
                      <td className="table-cell">
                        <div 
                          className="h-12 w-12 rounded-[1rem] bg-gray-100 flex items-center justify-center cursor-pointer overflow-hidden"
                          onClick={() => onPreview(image.previewUrl)}
                          role="button"
                          tabIndex={0}
                          aria-label={`Preview ${image.file.name}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onPreview(image.previewUrl);
                            }
                          }}
                        >
                          <img 
                            src={image.previewUrl} 
                            className="h-full w-full object-cover transition-all duration-200 cursor-pointer"
                            alt={`Preview of ${image.file.name}`}
                          />
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900 truncate max-w-[150px]">
                          {image.file.name}
                        </div>
                      </td>
                      <td className="table-cell">
                        {formatBytes(image.size)}
                      </td>
                      <td className="table-cell">
                        <span className={estimatedSize < image.size ? "text-green-600" : "text-red-600 font-medium flex items-center"}>
                          {estimatedSize >= image.size && (
                            <AlertTriangle size={14} className="mr-1 flex-shrink-0" />
                          )}
                          {estimatedSizeFormatted}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="tooltip-container">
                          <button
                            onClick={() => onRemove(image.id)}
                            className="action-button action-button-danger"
                            aria-label={`Remove ${image.file.name}`}
                          >
                            <X size={18} aria-hidden="true" />
                          </button>
                          <div className="tooltip">Remove image</div>
                        </div>
                      </td>
                    </tr>
                    {showProgress && (
                      <tr>
                        <td colSpan={5} className="p-0 align-top">
                          <div className="h-1 bg-gray-200 rounded-b overflow-hidden">
                            <div 
                              className="h-full bg-primary-500 transition-all duration-300"
                              style={{ width: `${progress}%` }}
                              role="progressbar"
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-label={`Converting ${image.file.name}`}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default UploadedImagesTable;